/**
 * HvacIcons.tsx — HVACloud Icon & Motion System (Kalibrier-Set)
 *
 * Framework: geschrieben für den AUTOMATISCHEN JSX-Runtime, daher kein
 * `import React`. Läuft unverändert in Preact, wenn in der tsconfig steht:
 *
 *   "jsx": "react-jsx",
 *   "jsxImportSource": "preact"
 *
 * Grid            24×24 (Heroes 48×48)
 * Stil            Outline 1.5px · butt caps · miter joins · Radius 1
 * Farbe           durchgehend currentColor — nie hart kodiert
 * Animationen     brauchen hvac-icons.css (einmal global importieren)
 */

type IconProps = {
  size?: number | string;
  strokeWidth?: number;
  title?: string;
  class?: string;
  className?: string;
  style?: any;
  [key: string]: any;
};

const BASE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'butt' as const,
  strokeLinejoin: 'miter' as const,
};

/** Gemeinsame SVG-Hülle: setzt Größe, Strichstärke und optionalen a11y-Titel. */
function Svg({
  size = 24,
  strokeWidth = 1.5,
  title,
  box = 24,
  children,
  ...rest
}: IconProps & { box?: number; children: any }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${box} ${box}`}
      strokeWidth={strokeWidth}
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : 'true'}
      {...BASE}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════
   01 · TOAST-STATUS  (animiert)
   Die vier universellen Töne. `animate={false}` für statische Nutzung.
   ══════════════════════════════════════════════════════════════ */

export function ToastSuccess({ animate = true, ...p }: IconProps & { animate?: boolean }) {
  return (
    <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
      <circle cx="12" cy="12" r="9" opacity=".35" />
      {animate && <circle cx="12" cy="12" r="9" class="hv-halo" />}
      <path d="M7.5 12.4 L10.6 15.4 L16.5 8.8" class={animate ? 'hv-draw' : undefined} />
    </Svg>
  );
}

export function ToastError({ animate = true, ...p }: IconProps & { animate?: boolean }) {
  return (
    <Svg {...p} class={animate ? 'hv-shake' : undefined} style={{ overflow: 'visible', ...(p.style || {}) }}>
      <circle cx="12" cy="12" r="9" opacity=".35" />
      {animate && <circle cx="12" cy="12" r="9" class="hv-halo" />}
      <path d="M8.6 8.6 L15.4 15.4" class={animate ? 'hv-draw' : undefined} />
      <path d="M15.4 8.6 L8.6 15.4" class={animate ? 'hv-draw hv-d2' : undefined} />
    </Svg>
  );
}

export function ToastWarn({ animate = true, ...p }: IconProps & { animate?: boolean }) {
  return (
    <Svg {...p}>
      <path d="M12 3.6 L21.4 19.8 L2.6 19.8 Z" opacity={animate ? '.3' : '1'} />
      {animate && <path d="M12 3.6 L21.4 19.8 L2.6 19.8 Z" class="hv-blink" />}
      <path d="M12 9 V14" />
      <rect x="11.25" y="16" width="1.5" height="1.5" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** running={true} dreht den Ring — für „Discovery läuft“, „Scan läuft“. */
export function ToastInfo({ running = true, ...p }: IconProps & { running?: boolean }) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="9" opacity={running ? '.25' : '1'} />
      {running && <path d="M12 3 A9 9 0 0 1 21 12" class="hv-spin" />}
      <rect x="11.25" y="7" width="1.5" height="1.5" fill="currentColor" stroke="none" />
      <path d="M12 11 V16.5" />
    </Svg>
  );
}

/* ══════════════════════════════════════════════════════════════
   02 · KI-VOKABULAR — die HVACloud-Wolke IST das KI-Signal
   Ein Glyph, drei Zustände. Immer mit --ai-accent einfärben:
     <AiCloud state="working" style={{ color: 'var(--ai-accent)' }} />
   ══════════════════════════════════════════════════════════════ */

/** Wolken-Silhouette (48er Grid) — identische Konstruktion wie die Wortmarke. */
function CloudBody({ opacity = 1, class: cls }: { opacity?: number | string; class?: string }) {
  return (
    <g fill="currentColor" stroke="none" opacity={opacity} class={cls}>
      <rect x="8" y="26" width="32" height="12" rx="6" />
      <circle cx="24" cy="22" r="10" />
      <circle cx="14" cy="26" r="8" />
      <circle cx="34" cy="26" r="8" />
    </g>
  );
}

const spark = (cx: number, cy: number, r: number) => {
  const i = r * 0.28;
  return `M${cx} ${cy - r} L${cx + i} ${cy - i} L${cx + r} ${cy} L${cx + i} ${cy + i} L${cx} ${cy + r} L${cx - i} ${cy + i} L${cx - r} ${cy} L${cx - i} ${cy - i} Z`;
};

export function AiCloud({ state = 'idle', ...p }: IconProps & { state?: 'idle' | 'working' | 'done' }) {
  return (
    <Svg box={48} size={p.size ?? 48} {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
      {state === 'idle' && (
        <g class="hv-float">
          <CloudBody opacity=".22" />
          <path d={spark(24, 19, 5.5)} fill="currentColor" stroke="none" opacity=".95" />
          <path d={spark(33, 26.5, 2.5)} fill="currentColor" stroke="none" opacity=".6" />
          <path d={spark(15, 27.25, 2.25)} fill="currentColor" stroke="none" opacity=".45" />
        </g>
      )}

      {state === 'working' && (
        <>
          <CloudBody opacity=".26" />
          <CloudBody class="hv-cloud-glow" />
          {/* Datenpunkte steigen von unten in die Wolke */}
          <g fill="var(--status-info)" stroke="none">
            {[15.9, 22.9, 29.9].map((x, i) => (
              <rect key={x} x={x} y="40" width="2.2" height="2.2" class="hv-rise" style={{ animationDelay: `${i * 0.5}s` }} />
            ))}
          </g>
          {/* Sparkles kreisen um die Wolke */}
          <g class="hv-orbit">
            <path d={spark(24, 10, 6)} fill="currentColor" stroke="none" />
            <path d={spark(41.5, 34, 4)} fill="currentColor" stroke="none" opacity=".8" />
            <path d={spark(6.5, 34.5, 3.5)} fill="currentColor" stroke="none" opacity=".55" />
          </g>
        </>
      )}

      {state === 'done' && (
        <>
          <g class="hv-puff" style={{ transformOrigin: '24px 25px' }}>
            <rect x="5" y="9" width="2.4" height="2.4" fill="var(--status-ok)" stroke="none" />
            <rect x="40" y="12" width="2.4" height="2.4" fill="var(--status-info)" stroke="none" />
            <rect x="9" y="39" width="2.4" height="2.4" fill="var(--status-warn)" stroke="none" />
            <rect x="38" y="37" width="2.4" height="2.4" fill="var(--status-ok)" stroke="none" />
            <rect x="23" y="3" width="2.4" height="2.4" fill="currentColor" stroke="none" />
          </g>
          <CloudBody opacity=".3" />
          <path d="M15.5 26.5 L21.5 32.5 L33 20" stroke="var(--accent-muted)" strokeWidth="3.4" class="hv-draw" />
        </>
      )}
    </Svg>
  );
}

/* Die vier konkreten KI-Triggerpunkte. Der Sparkle oben rechts ist konstant
   der Absender — die Bewegung darunter zeigt, WAS entsteht. */

const senderSpark = <path d={spark(42, 7.5, 5.5)} fill="currentColor" stroke="none" class="hv-twinkle" style={{ transformOrigin: '42px 7.5px' }} />;

/** Baum baut sich Ebene für Ebene. */
export const AiSchema = (p: IconProps) => (
  <Svg box={48} size={p.size ?? 48} {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <path d="M10 9 V38" stroke="var(--text-secondary)" opacity=".4" />
    {[15, 24, 33].map((y, i) => (
      <g key={y} class="hv-grow" style={{ animationDelay: `${i * 0.5}s` }}>
        <path d={`M10 ${y} H19`} />
        <rect x="19" y={y - 3} width="13" height="6" />
      </g>
    ))}
    {senderSpark}
  </Svg>
);

/** Einheiten-Chips fliegen an die Datenpunkte. */
export const AiUnits = (p: IconProps) => (
  <Svg box={48} size={p.size ?? 48} {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <g stroke="var(--text-secondary)" opacity=".45">
      {[15, 24, 33].map((cy) => <circle key={cy} cx="11" cy={cy} r="2.6" />)}
    </g>
    {[15, 24, 33].map((cy, i) => (
      <g key={cy} class="hv-fly" style={{ animationDelay: `${i * 0.35}s` }}>
        <rect x="18" y={cy - 3.5} width="17" height="7" rx="1" />
        <path d={`M22 ${cy} H31`} opacity=".55" />
      </g>
    ))}
    {senderSpark}
  </Svg>
);

/** Widget-Grid setzt sich zusammen. */
export const AiDashboard = (p: IconProps) => (
  <Svg box={48} size={p.size ?? 48} {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <rect x="6" y="10" width="36" height="29" stroke="var(--text-secondary)" opacity=".4" />
    <g fill="currentColor" stroke="none">
      {([[10, 14, 12, 9, 0.85], [25, 14, 13, 9, 0.7], [10, 26, 7, 9, 0.55], [20, 26, 18, 9, 0.7]] as const).map(
        ([x, y, w, h, o], i) => (
          <rect key={i} x={x} y={y} width={w} height={h} opacity={o} class="hv-grow" style={{ animationDelay: `${i * 0.4}s` }} />
        )
      )}
    </g>
    {senderSpark}
  </Svg>
);

/** Line-Chart zeichnet sich ein. */
export const AiChartPreset = (p: IconProps) => (
  <Svg box={48} size={p.size ?? 48} {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <path d="M8 9 V38 H42" stroke="var(--text-secondary)" opacity=".4" />
    <path d="M8 31 H42 M8 23 H42 M8 15 H42" stroke="var(--text-secondary)" opacity=".14" />
    <path d="M10 32 L17 22 L23 27 L30 14 L36 20 L41 11" strokeWidth="2" class="hv-chart" />
    {senderSpark}
  </Svg>
);

/* ══════════════════════════════════════════════════════════════
   03 · PROZESS-ANIMATIONEN
   ══════════════════════════════════════════════════════════════ */

/** Client-Sync-Overlay: rotierender Ring + pulsierender Punkt. */
export function SyncSpinner(p: IconProps) {
  return (
    <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
      <circle cx="12" cy="12" r="9" opacity=".2" />
      <path d="M12 3 A9 9 0 0 1 20.4 8.8" class="hv-spin" />
      <circle cx="12" cy="12" r="2.4" fill="currentColor" stroke="none" class="hv-dot" />
    </Svg>
  );
}

/** Discovery-Radar-Sweep für das Running-Banner. */
export function DiscoveryRadar(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="9.5" opacity=".22" />
      <circle cx="12" cy="12" r="6" opacity=".22" />
      <circle cx="12" cy="12" r="2.5" opacity=".22" />
      <g class="hv-sweep">
        <path d="M12 12 L12 2.5" />
        <path d="M12 12 L12 2.5 A9.5 9.5 0 0 1 19 5" opacity=".35" />
      </g>
      <rect x="15.4" y="7.4" width="1.8" height="1.8" fill="var(--status-ok)" stroke="none" />
    </Svg>
  );
}

/** Command-Ack: Wert wandert in den Regler, Häkchen bestätigt. */
export function CommandAck(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="2.5" y="7" width="9" height="10" />
      <path d="M5 10.5 H9" />
      <path d="M5 13.5 H9" opacity=".5" />
      <g class="hv-ack">
        <path d="M12.5 12 H18" />
        <path d="M15.8 9.4 L18.4 12 L15.8 14.6" />
      </g>
      <path d="M18.4 17.6 L20.2 19.4 L23 15.8" stroke="var(--status-ok)" class="hv-draw hv-late" />
    </Svg>
  );
}

/** GeoPicker: Pin fällt mit Bounce, Ripple am Boden. */
export const GeoPinDrop = (p: IconProps) => (
  <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <path d="M4 20 H20" opacity=".35" />
    <ellipse cx="12" cy="20" rx="4" ry="1.4" class="hv-ripple" />
    <g class="hv-pin">
      <path d="M12 17 C12 17 6.5 11.5 6.5 8 A5.5 5.5 0 0 1 17.5 8 C17.5 11.5 12 17 12 17 Z" />
      <circle cx="12" cy="8" r="2" fill="currentColor" stroke="none" />
    </g>
  </Svg>
);

/** Template-Apply: Baum füllt sich Blatt für Blatt. */
export const TemplateApplying = (p: IconProps) => (
  <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <path d="M4 4 V20" opacity=".4" />
    {[8, 14, 20].map((y, i) => (
      <g key={y} class="hv-grow" style={{ animationDelay: `${i * 0.5}s` }}>
        <path d={`M4 ${y} H9`} />
        <rect x="9" y={y - 2} width="8" height="4" />
      </g>
    ))}
  </Svg>
);

/** Credentials-Test: Schlüssel dreht, dann Häkchen. */
export const CredentialsTest = (p: IconProps) => (
  <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <g class="hv-key" style={{ transformOrigin: '8px 12px' }}>
      <circle cx="8" cy="12" r="3.6" />
      <path d="M11.6 12 H19" />
      <path d="M16 12 V15.5 M18.5 12 V14.5" />
    </g>
    <path d="M15.5 19 L17.5 21 L21.5 16.5" stroke="var(--status-ok)" strokeWidth="1.8" class="hv-draw hv-late" />
  </Svg>
);

/** Classify-Candidate: Fragezeichen-Chip morpht in Chip-mit-Label. */
export const ClassifyMorph = (p: IconProps) => (
  <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <g class="hv-morph-out">
      <rect x="4" y="8.5" width="16" height="7" rx="1" strokeDasharray="3 2" />
      <path d="M10.4 11 A1.7 1.7 0 0 1 13.2 12.3 C13.2 13.2 12 13.3 12 14" />
    </g>
    <g class="hv-morph-in" stroke="var(--status-ok)">
      <rect x="4" y="8.5" width="16" height="7" rx="1" />
      <path d="M7.5 12 H11" />
      <rect x="13" y="10.5" width="4" height="3" fill="var(--status-ok)" stroke="none" />
    </g>
  </Svg>
);

/** Timeseries-Ingest: Daten strömen live ein. */
export const TimeseriesIngest = (p: IconProps) => (
  <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <path d="M3 20 H21" opacity=".3" />
    <path d="M3 16 Q6 9 9 13 T15 8 T21 11" strokeDasharray="4 2" class="hv-stream" />
    <circle cx="21" cy="11" r="2" fill="currentColor" stroke="none" class="hv-dot" />
  </Svg>
);

/** Alarm-Trigger: Glocke wippt + Ripple. */
export const AlarmTrigger = (p: IconProps) => (
  <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <circle cx="12" cy="12" r="10" class="hv-ripple" />
    <g class="hv-bounce">
      <path d="M12 4 A6 6 0 0 1 18 10 V15 H6 V10 A6 6 0 0 1 12 4 Z" />
      <path d="M10 18 A2 2 0 0 0 14 18" />
    </g>
  </Svg>
);

/** Netzwerk-Scan: Zeile wandert, Geräte poppen auf. */
export const NetworkScan = (p: IconProps) => (
  <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <rect x="3" y="5" width="18" height="14" opacity=".35" />
    <g class="hv-scan-v"><path d="M3.5 6 H20.5" /></g>
    <g fill="var(--status-ok)" stroke="none">
      {([[6, 8.5], [13, 11], [9, 14.5]] as const).map(([x, y], i) => (
        <rect key={i} x={x} y={y} width="3" height="3" class="hv-pop" style={{ animationDelay: `${i * 0.5}s` }} />
      ))}
    </g>
  </Svg>
);

/** Schema-Sync: zwei Seiten gleichen ab. */
export const SchemaSync = (p: IconProps) => (
  <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <path d="M4 5 V19" opacity=".4" />
    <path d="M20 5 V19" opacity=".4" />
    <g class="hv-sync-rot">
      <path d="M7 9 H17" />
      <path d="M14.5 6.5 L17 9 L14.5 11.5" />
      <path d="M17 15 H7" />
      <path d="M9.5 12.5 L7 15 L9.5 17.5" />
    </g>
  </Svg>
);

/** Force aktiv: Schloss pulst mit Ripple. */
export const ForceActive = (p: IconProps) => (
  <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <rect x="5" y="11" width="14" height="9" />
    <path d="M8 11 V8 A4 4 0 0 1 16 8 V11" />
    <rect x="11" y="14" width="2" height="3.5" fill="currentColor" stroke="none" class="hv-lock" />
    <circle cx="12" cy="15.5" r="6.5" class="hv-ripple" />
  </Svg>
);

/** Reconnect: Balken suchen die Verbindung. */
export const Reconnect = (p: IconProps) => (
  <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <path d="M12 20 A8 8 0 0 1 12 4" opacity=".25" />
    <path d="M6 14 A8.5 8.5 0 0 1 18 14" class="hv-wifi" />
    <path d="M8.5 16.5 A5 5 0 0 1 15.5 16.5" class="hv-wifi" style={{ animationDelay: '.35s' }} />
    <circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none" class="hv-wifi" style={{ animationDelay: '.7s' }} />
  </Svg>
);

/** Aufräumen: Besen wischt, Reste verschwinden. */
export const CleanupRunning = (p: IconProps) => (
  <Svg {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    <path d="M3 20 H21" opacity=".35" />
    <g class="hv-broom" style={{ transformOrigin: '12px 8px' }}>
      <path d="M12 4 V11" />
      <path d="M8.5 18 L10 11 H14 L15.5 18 Z" />
      <path d="M10 14.5 H14" opacity=".5" />
    </g>
    <g fill="var(--text-muted)" stroke="none">
      <rect x="4.5" y="17.5" width="1.6" height="1.6" class="hv-pop" />
      <rect x="18" y="17.5" width="1.6" height="1.6" class="hv-pop" style={{ animationDelay: '.7s' }} />
    </g>
  </Svg>
);

/** Chart-Skeleton mit Shimmer — für ladende Auswertungen. */
export function ChartSkeleton({ rows = [100, 78, 56, 88] }: { rows?: number[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {rows.map((w, i) => (
        <div
          key={i}
          style={{
            height: '7px',
            width: `${w}%`,
            borderRadius: 'var(--radius-1)',
            background: 'var(--surface-elevated)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <div class="hv-shimmer" style={{ position: 'absolute', inset: 0, width: '45%', animationDelay: `${i * 0.25}s` }} />
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   08 · DIALOG-HEROES  (96×96 Grid, gerendert 96–128px)
   Prominent oben im Modal. Mehr Detail, längere Erzählung als die 24er.
   ══════════════════════════════════════════════════════════════ */

const Hero = (p: IconProps & { children: any }) => (
  <Svg box={96} size={p.size ?? 96} {...p} style={{ overflow: 'visible', ...(p.style || {}) }}>
    {p.children}
  </Svg>
);

/** TriggerScanModal — Radar-Sweep, Geräte tauchen auf. */
export const HeroNetworkScan = (p: IconProps) => (
  <Hero {...p}>
    <circle cx="48" cy="48" r="42" opacity=".18" />
    <circle cx="48" cy="48" r="29" opacity=".18" />
    <circle cx="48" cy="48" r="16" opacity=".18" />
    <g class="hv-sweep-hero">
      <path d="M48 48 L48 6" />
      <path d="M48 48 L48 6 A42 42 0 0 1 78 18 Z" fill="currentColor" stroke="none" opacity=".12" />
    </g>
    <rect x="45" y="45" width="6" height="6" fill="currentColor" stroke="none" />
    <g fill="var(--status-ok)" stroke="none">
      {([[62, 26], [24, 34], [58, 62], [30, 60]] as const).map(([x, y], i) => (
        <rect key={i} x={x} y={y} width="7" height="7" class="hv-pop-slow" style={{ animationDelay: `${i * 0.6}s` }} />
      ))}
    </g>
  </Hero>
);

/** SubmitDiscoveryModal — Baum füllt sich, dann Bestätigung. */
export const HeroSubmitDiscovery = (p: IconProps) => (
  <Hero {...p}>
    <path d="M22 14 V80" stroke="var(--text-secondary)" opacity=".4" />
    {[24, 42, 60, 78].map((y, i) => (
      <g key={y} class="hv-grow-slow" style={{ animationDelay: `${i * 0.6}s` }}>
        <path d={`M22 ${y} H40`} />
        <rect x="40" y={y - 6} width="26" height="12" />
        <path d={`M46 ${y} H60`} opacity=".45" />
      </g>
    ))}
    <path d="M74 62 L80 68 L92 54" stroke="var(--accent-muted)" strokeWidth="3" class="hv-draw hv-draw-hero" />
  </Hero>
);

/** TriggerRolloutModal — Wolke → Clients, Progress-Ring. */
export const HeroRollout = (p: IconProps) => (
  <Hero {...p}>
    <circle cx="48" cy="26" r="24" strokeWidth="2.5" opacity=".18" />
    <circle cx="48" cy="26" r="24" strokeWidth="2.5" strokeDasharray="232" transform="rotate(-90 48 26)" class="hv-ring" />
    <g fill="currentColor" stroke="none" opacity=".9">
      <rect x="34" y="26" width="28" height="9" rx="4.5" />
      <circle cx="48" cy="23" r="8" />
      <circle cx="39" cy="26" r="6.5" />
      <circle cx="57" cy="26" r="6.5" />
    </g>
    <g strokeDasharray="4 3" class="hv-flow">
      <path d="M28 56 V66" /><path d="M48 56 V66" /><path d="M68 56 V66" />
    </g>
    <g>
      <rect x="18" y="68" width="20" height="16" />
      <rect x="38" y="68" width="20" height="16" />
      <rect x="58" y="68" width="20" height="16" />
    </g>
    <g fill="var(--accent-muted)" stroke="none">
      {[25, 45, 65].map((x, i) => (
        <rect key={x} x={x} y="74" width="6" height="4" class="hv-pop" style={{ animationDelay: `${i * 0.5}s` }} />
      ))}
    </g>
  </Hero>
);

/** AiDocumentationParserModal — Dokumentseite mit gescannten Zeilen,
 *  extrahierte Register wandern rechts in eine wachsende Baum-Silhouette.
 *  Zwei parallele Animationen: eine helle Scan-Line läuft von oben nach unten
 *  über die Doc-Zeilen (hv-scanline), gleichzeitig sprossen rechts die Nodes
 *  gestaffelt (hv-grow-slow). */
export const HeroRegisterExtract = (p: IconProps) => (
  <Hero {...p}>
    {/* Dokumentseite links */}
    <rect x="8" y="10" width="34" height="76" rx="2" opacity=".5" />
    {/* Titelzeile */}
    <path d="M12 18 H36" strokeWidth="2" opacity=".55" />
    <path d="M12 22 H28" opacity=".35" />
    {/* Tabellenzeilen (die "Register" im Dokument) */}
    {[32, 42, 52, 62, 72].map((y, i) => (
      <g key={y} opacity=".85">
        <path d={`M12 ${y} H24`} class="hv-grow-slow" style={{ animationDelay: `${i * 0.35}s` }} />
        <path d={`M26 ${y} H36`} opacity=".55" class="hv-grow-slow" style={{ animationDelay: `${i * 0.35 + 0.15}s` }} />
      </g>
    ))}
    {/* Scan-Line die über die Seite läuft */}
    <rect
      x="8"
      y="24"
      width="34"
      height="3"
      fill="var(--accent, currentColor)"
      opacity=".55"
      stroke="none"
      class="hv-scanline"
    />
    {/* Fluss-Pfeile aus der Doku heraus */}
    <g stroke="var(--text-secondary)" strokeDasharray="3 3" class="hv-flow" opacity=".7">
      <path d="M44 30 H56" />
      <path d="M44 48 H56" />
      <path d="M44 66 H56" />
    </g>
    {/* Baum-Silhouette rechts (wächst gestaffelt) */}
    <path d="M58 18 V78" stroke="var(--text-secondary)" opacity=".4" />
    {[26, 40, 54, 68].map((y, i) => (
      <g key={y} class="hv-grow-slow" style={{ animationDelay: `${0.6 + i * 0.5}s` }}>
        <path d={`M58 ${y} H68`} />
        <rect x="68" y={y - 5} width="20" height="10" rx="1.5" />
      </g>
    ))}
    {/* Häkchen unten — "extrahiert" */}
    <path d="M76 82 L80 86 L90 76" stroke="var(--accent-muted, currentColor)" strokeWidth="3" class="hv-draw hv-draw-hero" />
  </Hero>
);

/** AiSchemaModal — Wolke denkt, Struktur entsteht. Mit --ai-accent einfärben. */
export const HeroAiSchema = (p: IconProps) => (
  <Hero {...p}>
    <g fill="currentColor" stroke="none" opacity=".24">
      <rect x="10" y="30" width="42" height="16" rx="8" /><circle cx="31" cy="25" r="13" /><circle cx="18" cy="30" r="10.5" /><circle cx="44" cy="30" r="10.5" />
    </g>
    <g fill="currentColor" stroke="none" class="hv-cloud-glow">
      <rect x="10" y="30" width="42" height="16" rx="8" /><circle cx="31" cy="25" r="13" /><circle cx="18" cy="30" r="10.5" /><circle cx="44" cy="30" r="10.5" />
    </g>
    <path d={spark(31, 12, 8)} fill="currentColor" stroke="none" class="hv-twinkle" style={{ transformOrigin: '31px 12px' }} />
    <g strokeDasharray="4 3" class="hv-flow"><path d="M52 38 H64" /></g>
    <path d="M64 20 V76" stroke="var(--text-secondary)" opacity=".4" />
    {[30, 48, 66].map((y, i) => (
      <g key={y} class="hv-grow" style={{ animationDelay: `${i * 0.5}s` }}>
        <path d={`M64 ${y} H74`} />
        <rect x="74" y={y - 5} width="16" height="10" />
      </g>
    ))}
  </Hero>
);

/** CredentialsModal — Schlüssel dreht, Test bestätigt. */
export const HeroCredentials = (p: IconProps) => (
  <Hero {...p}>
    <rect x="30" y="44" width="40" height="30" opacity=".45" />
    <path d="M39 44 V34 A11 11 0 0 1 61 34 V44" opacity=".45" />
    <g class="hv-key-hero" style={{ transformOrigin: '50px 58px' }}>
      <circle cx="50" cy="58" r="9" strokeWidth="2" />
      <circle cx="50" cy="58" r="3" fill="currentColor" stroke="none" />
      <path d="M59 58 H82" strokeWidth="2" />
      <path d="M72 58 V68 M78 58 V65" strokeWidth="2" />
    </g>
    <path d="M14 66 L22 74 L36 56" stroke="var(--accent-muted)" strokeWidth="3" class="hv-draw hv-draw-hero2" />
  </Hero>
);

/** SendCommandModal — Regler fährt, Ack quittiert. */
export const HeroSendCommand = (p: IconProps) => (
  <Hero {...p}>
    <path d="M14 46 H82" opacity=".3" />
    <g opacity=".45"><path d="M14 40 V52 M38 40 V52 M62 40 V52 M82 40 V52" /></g>
    <g class="hv-handle">
      <rect x="32" y="36" width="12" height="20" rx="2" fill="var(--surface-panel)" strokeWidth="2" />
      <path d="M38 41 V51" opacity=".6" />
    </g>
    <g class="hv-fly-slow">
      <rect x="52" y="14" width="30" height="14" rx="2" />
      <path d="M58 21 H70" />
      <rect x="73" y="19" width="4" height="4" fill="currentColor" stroke="none" />
    </g>
    <path d="M56 70 L64 78 L80 60" stroke="var(--accent-muted)" strokeWidth="3" class="hv-draw hv-draw-hero3" />
  </Hero>
);

/** GeoPickerModal — Pin fällt auf die Karte. */
export const HeroGeoPicker = (p: IconProps) => (
  <Hero {...p}>
    <rect x="10" y="20" width="76" height="60" stroke="var(--text-secondary)" opacity=".35" />
    <g stroke="var(--text-secondary)" opacity=".16">
      <path d="M29 20 V80 M48 20 V80 M67 20 V80 M10 40 H86 M10 60 H86" />
    </g>
    <path d="M10 66 Q30 54 48 62 T86 52" stroke="var(--text-secondary)" opacity=".3" />
    <ellipse cx="48" cy="66" rx="14" ry="4.5" class="hv-ripple-slow" />
    <g class="hv-pin-hero">
      <path d="M48 62 C48 62 32 45 32 33 A16 16 0 0 1 64 33 C64 45 48 62 48 62 Z" fill="var(--surface-panel)" strokeWidth="2" />
      <circle cx="48" cy="33" r="6" fill="currentColor" stroke="none" />
    </g>
  </Hero>
);

/** Kickstarter/Enrollment — SSH → Install → Enable → Ready. */
export const HeroProvisioning = (p: IconProps) => (
  <Hero {...p}>
    <path d="M20 14 V82" stroke="var(--text-secondary)" opacity=".25" strokeDasharray="3 4" />
    <path d="M20 14 V82" strokeDasharray="210" class="hv-draw-l" />
    {([[20, 34, false], [40, 42, false], [60, 30, false], [80, 38, true]] as const).map(([cy, w, last], i) => (
      <g key={cy} class="hv-grow-slow" style={{ animationDelay: `${i * 0.6}s` }}>
        <circle cx="20" cy={cy} r="7" fill={last ? 'var(--accent-muted)' : 'var(--surface-panel)'} stroke={last ? 'none' : 'currentColor'} strokeWidth="2" />
        <path d={`M16.5 ${cy + 0.5} L19 ${cy + 3} L24 ${cy - 2.5}`} stroke={last ? 'var(--surface-base)' : 'currentColor'} strokeWidth="2" />
        <rect x="34" y={cy - 5} width={w} height="10" rx="1" stroke={last ? 'var(--accent-muted)' : 'currentColor'} opacity={last ? 1 : 0.55} />
      </g>
    ))}
  </Hero>
);

/** Rollout-Progress: Balken mit gleitendem Highlight. `value` 0–100. */
export function RolloutProgress({ value = 38, height = 4 }: { value?: number; height?: number }) {
  return (
    <div
      style={{
        height: `${height}px`,
        background: 'var(--surface-elevated)',
        borderRadius: 'var(--radius-1)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div style={{ position: 'absolute', inset: 0, width: `${value}%`, background: 'var(--accent)' }} />
      <div class="hv-slide" style={{ position: 'absolute', inset: 0, width: '26%' }} />
    </div>
  );
}

/** Provisioning: mehrstufige Check-Kaskade (SSH → Install → Enable → Ready). */
export function ProvisioningCascade({
  steps = ['SSH', 'Install', 'Enable', 'Ready'],
  done = -1,
}: {
  steps?: string[];
  /** Index des letzten fertigen Schritts; -1 = alle laufen als Loop-Demo. */
  done?: number;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {steps.map((s, i) => {
        const isDone = done >= i;
        return (
          <div
            key={s}
            class={done < 0 ? 'hv-step' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              animationDelay: done < 0 ? `${i * 0.8}s` : undefined,
              opacity: done < 0 ? undefined : isDone ? 1 : 0.35,
            }}
          >
            <Svg size={13} strokeWidth={2.4} style={{ color: 'var(--accent)', flex: 'none' }}>
              <path d="M5 12.4 L9.5 16.9 L19 6.5" />
            </Svg>
            <span style={{ fontSize: 'var(--fs-xs)', fontFamily: 'var(--font-mono)' }}>{s}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   04 · STATUS-BADGE
   ══════════════════════════════════════════════════════════════ */

export type StatusTone = 'current' | 'syncing' | 'running' | 'error' | 'none' | 'command' | 'force';

const STATUS: Record<StatusTone, { label: string; color: string; pulse?: boolean; outline?: boolean }> = {
  current: { label: 'aktuell', color: 'var(--status-ok)' },
  syncing: { label: 'syncing…', color: 'var(--status-warn)', pulse: true },
  running: { label: 'läuft', color: 'var(--status-info)', pulse: true },
  error: { label: 'Fehler', color: 'var(--status-error)' },
  none: { label: 'keine', color: 'var(--text-muted)' },
  command: { label: 'Command', color: 'var(--status-info)' },
  force: { label: 'Force aktiv', color: 'var(--status-error)', outline: true },
};

export function StatusBadge({ tone, label }: { tone: StatusTone; label?: string }) {
  const s = STATUS[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        background: 'var(--surface-panel)',
        border: `1px solid ${s.outline ? s.color : 'var(--border-subtle)'}`,
        borderRadius: 'var(--radius-1)',
        padding: '7px 12px',
        fontSize: 'var(--fs-sm)',
        color: s.outline ? s.color : 'var(--text-primary)',
      }}
    >
      <span
        class={s.pulse ? 'hv-dot' : undefined}
        style={{
          width: '7px',
          height: '7px',
          borderRadius: '50%',
          flex: 'none',
          boxSizing: 'border-box',
          background: s.outline ? 'transparent' : s.color,
          border: s.outline ? `1.5px solid ${s.color}` : undefined,
        }}
      />
      {label ?? s.label}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════
   05 · SUB-NAV-TABS  (Plant-Bereich)
   ══════════════════════════════════════════════════════════════ */

export const NavDatapoints = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 4 V20 H20" />
    <rect x="7" y="13" width="3" height="4" fill="currentColor" stroke="none" />
    <rect x="12" y="9" width="3" height="8" fill="currentColor" stroke="none" />
    <rect x="17" y="6" width="3" height="11" fill="currentColor" stroke="none" opacity=".5" />
  </Svg>
);

export const NavAnalysis = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 4 V20 H20" />
    <path d="M7 16 L11 10 L14 13 L20 5" />
  </Svg>
);

export const NavHmi = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="14" />
    <circle cx="9" cy="11" r="2.5" />
    <path d="M14 9 H18" />
    <path d="M14 13 H18" />
  </Svg>
);

export const NavAlarms = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3 L21 19 H3 Z" />
    <path d="M12 9 V13.5" />
    <rect x="11.25" y="15.4" width="1.5" height="1.5" fill="currentColor" stroke="none" />
  </Svg>
);

export const NavDevices = (p: IconProps) => (
  <Svg {...p}>
    <rect x="6" y="6" width="12" height="12" />
    <rect x="10" y="10" width="4" height="4" fill="currentColor" stroke="none" />
    <path d="M9 6 V3 M15 6 V3 M9 18 V21 M15 18 V21 M6 9 H3 M6 15 H3 M18 9 H21 M18 15 H21" />
  </Svg>
);

export const NavUpdate = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 16 V4" />
    <path d="M8 8 L12 4 L16 8" />
    <path d="M4 16 V20 H20 V16" />
  </Svg>
);

export const NavShell = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="16" />
    <path d="M7 10 L10 12.5 L7 15" />
    <path d="M12.5 15 H17" />
  </Svg>
);

/* ══════════════════════════════════════════════════════════════
   06 · CLIENT-TYPE HEROES  (48×48)
   ══════════════════════════════════════════════════════════════ */

export const ClientLinuxGateway = (p: IconProps) => (
  <Svg box={48} size={p.size ?? 48} {...p}>
    <path d="M4 34 H44" opacity=".4" />
    <path d="M4 37 H44" opacity=".4" />
    <rect x="11" y="12" width="26" height="22" />
    <path d="M16 12 V34 M22 12 V34 M28 12 V34" opacity=".45" />
    <rect x="30.5" y="16" width="4" height="4" fill="currentColor" stroke="none" />
    <path d="M30.5 24 H34.5 M30.5 28 H34.5" />
  </Svg>
);

export const ClientAndroidPanel = (p: IconProps) => (
  <Svg box={48} size={p.size ?? 48} {...p}>
    <rect x="6" y="10" width="36" height="28" />
    <rect x="11" y="15" width="26" height="18" opacity=".35" />
    <path d="M18 27 A6 6 0 0 1 30 27" />
    <path d="M17 20 L19.5 23.5 M31 20 L28.5 23.5" />
    <rect x="20.5" y="24.5" width="1.6" height="1.6" fill="currentColor" stroke="none" />
    <rect x="25.9" y="24.5" width="1.6" height="1.6" fill="currentColor" stroke="none" />
  </Svg>
);

export const ClientWindowsPc = (p: IconProps) => (
  <Svg box={48} size={p.size ?? 48} {...p}>
    <rect x="10" y="9" width="28" height="30" />
    <path d="M23 15 H33 M23 19 H33" opacity=".45" />
    <rect x="15" y="14" width="4" height="4" fill="currentColor" stroke="none" />
    <path d="M15 24 H33" opacity=".3" />
    <path d="M17 29 H21 M17 33 H21" />
    <rect x="27" y="28" width="6" height="6" />
  </Svg>
);

export const ClientIosPanel = (p: IconProps) => (
  <Svg box={48} size={p.size ?? 48} {...p}>
    <rect x="12" y="6" width="24" height="36" rx="2" />
    <rect x="15.5" y="11" width="17" height="24" opacity=".35" />
    <circle cx="24" cy="38.5" r="1.5" />
    <path d="M21 8.5 H27" />
  </Svg>
);

export const ClientEmbedded = (p: IconProps) => (
  <Svg box={48} size={p.size ?? 48} {...p}>
    <rect x="14" y="14" width="20" height="20" />
    <rect x="20" y="20" width="8" height="8" fill="currentColor" stroke="none" opacity=".6" />
    <path d="M19 14 V8 M24 14 V8 M29 14 V8 M19 34 V40 M24 34 V40 M29 34 V40 M14 19 H8 M14 24 H8 M14 29 H8 M34 19 H40 M34 24 H40 M34 29 H40" />
  </Svg>
);

/* ══════════════════════════════════════════════════════════════
   07 · PRIMARY ACTIONS  (24×24)
   ══════════════════════════════════════════════════════════════ */

export const IconSave = (p: IconProps) => (
  <Svg {...p}><path d="M4 4 H16 L20 8 V20 H4 Z" /><path d="M8 4 V10 H15 V4" /><rect x="8" y="14" width="8" height="6" /></Svg>
);
export const IconCreate = (p: IconProps) => (
  <Svg {...p}><path d="M12 5 V19" /><path d="M5 12 H19" /></Svg>
);
export const IconApply = (p: IconProps) => (
  <Svg {...p}><path d="M4 12.5 L9.5 18 L20 6" /></Svg>
);
export const IconDeleteHard = (p: IconProps) => (
  <Svg {...p}><path d="M4 7 H20" /><path d="M9 7 V4 H15 V7" /><path d="M6 7 L7 20 H17 L18 7" /><path d="M10 11 V16 M14 11 V16" /></Svg>
);
export const IconDeleteSoft = (p: IconProps) => (
  <Svg {...p}><path d="M4 7 H20" opacity=".55" /><path d="M9 7 V4 H15 V7" opacity=".55" /><path d="M6 7 L7 20 H17 L18 7" opacity=".55" /><path d="M9.5 13.5 H14.5" /></Svg>
);
export const IconRename = (p: IconProps) => (
  <Svg {...p}><path d="M4 20 H20" opacity=".45" /><path d="M6 16 L16 6 L19 9 L9 19 H6 Z" /></Svg>
);
export const IconMove = (p: IconProps) => (
  <Svg {...p}><path d="M12 3 V21 M3 12 H21" /><path d="M12 3 L9.5 5.5 M12 3 L14.5 5.5 M12 21 L9.5 18.5 M12 21 L14.5 18.5 M3 12 L5.5 9.5 M3 12 L5.5 14.5 M21 12 L18.5 9.5 M21 12 L18.5 14.5" /></Svg>
);
export const IconShare = (p: IconProps) => (
  <Svg {...p}><circle cx="6" cy="12" r="2.6" /><circle cx="17" cy="6.5" r="2.6" /><circle cx="17" cy="17.5" r="2.6" /><path d="M8.4 10.8 L14.6 7.7 M8.4 13.2 L14.6 16.3" /></Svg>
);
export const IconDownload = (p: IconProps) => (
  <Svg {...p}><path d="M12 4 V15" /><path d="M8 11 L12 15 L16 11" /><path d="M4 18 V20 H20 V18" /></Svg>
);
export const IconUpload = (p: IconProps) => (
  <Svg {...p}><path d="M12 20 V9" /><path d="M8 13 L12 9 L16 13" /><path d="M4 6 V4 H20 V6" /></Svg>
);
export const IconLogout = (p: IconProps) => (
  <Svg {...p}><path d="M10 4 H4 V20 H10" /><path d="M14 12 H21" /><path d="M17.5 8.5 L21 12 L17.5 15.5" /></Svg>
);
export const IconCleanup = (p: IconProps) => (
  <Svg {...p}><path d="M6 20 L10 10 H14 L18 20 Z" /><path d="M10 10 V4 H14 V10" /><path d="M8 15 H16" opacity=".5" /></Svg>
);
export const IconDiscovery = (p: IconProps) => (
  <Svg {...p}><circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.2 15.2 L20 20" /><path d="M8 10.5 H13 M10.5 8 V13" opacity=".55" /></Svg>
);
export const IconTemplateApply = (p: IconProps) => (
  <Svg {...p}><rect x="3" y="4" width="8" height="6" /><path d="M11 7 H16 V17 H13" opacity=".5" /><path d="M13 14 L16 17 L13 20" opacity=".5" /><rect x="3" y="14" width="8" height="6" /></Svg>
);
export const IconTemplateSave = (p: IconProps) => (
  <Svg {...p}><path d="M4 5 H14 L17 8 V19 H4 Z" /><path d="M9 9 H13 M9 12 H13 M9 15 H13" /><path d="M17 4 L18.2 7.3 L21.5 8.5 L18.2 9.7 L17 13 L15.8 9.7 L12.5 8.5 L15.8 7.3 Z" opacity=".7" /></Svg>
);
export const IconSchemaRemove = (p: IconProps) => (
  <Svg {...p}><path d="M4 6 H11" /><path d="M4 12 H9" /><path d="M4 18 H11" /><path d="M13 9 L21 17 M21 9 L13 17" /></Svg>
);
export const IconPickOnMap = (p: IconProps) => (
  <Svg {...p}><path d="M12 21 C12 21 5 14.5 5 10 A7 7 0 0 1 19 10 C19 14.5 12 21 12 21 Z" /><circle cx="12" cy="10" r="2.6" /></Svg>
);
export const IconTest = (p: IconProps) => (
  <Svg {...p}><path d="M9 3 H15" /><path d="M10 3 V10 L5.5 19 A1 1 0 0 0 6.5 20.5 H17.5 A1 1 0 0 0 18.5 19 L14 10 V3" /><path d="M7.5 15 H16.5" opacity=".5" /></Svg>
);
export const IconForce = (p: IconProps) => (
  <Svg {...p}><path d="M13 2 L5 13 H11 L10 22 L19 10 H12.5 Z" /></Svg>
);
export const IconForceRelease = (p: IconProps) => (
  <Svg {...p}><path d="M13 2 L5 13 H11 L10 22 L19 10 H12.5 Z" opacity=".35" /><path d="M4 20 L20 4" /></Svg>
);
export const IconEdit = (p: IconProps) => (
  <Svg {...p}><path d="M6 16 L16 6 L19 9 L9 19 H6 Z" /><path d="M14 8 L17 11" opacity=".5" /></Svg>
);
export const IconUpdate = (p: IconProps) => (
  <Svg {...p}><path d="M20 12 A8 8 0 1 1 12 4" /><path d="M12 1 L15 4 L12 7" /><path d="M12 9 V13 H15.5" /></Svg>
);
export const IconCancel = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="12" r="8.5" opacity=".45" /><path d="M8.5 8.5 L15.5 15.5 M15.5 8.5 L8.5 15.5" /></Svg>
);
export const IconClose = (p: IconProps) => (
  <Svg {...p}><path d="M6 6 L18 18 M18 6 L6 18" /></Svg>
);

/* ---- Werkzeugleiste der Datenpunkte-Ansicht (22.10.2026) ---------------- */
export const IconExpandAll = (p: IconProps) => (
  <Svg {...p}><path d="M7 5 L12 10 L17 5" /><path d="M7 13 L12 18 L17 13" /><path d="M4 21 H20" opacity=".45" /></Svg>
);
export const IconCollapseAll = (p: IconProps) => (
  <Svg {...p}><path d="M7 10 L12 5 L17 10" /><path d="M7 18 L12 13 L17 18" /><path d="M4 21 H20" opacity=".45" /></Svg>
);
export const IconHistory = (p: IconProps) => (
  <Svg {...p}><path d="M4 12 A8 8 0 1 0 6.3 6.3" /><path d="M3 4 V8 H7" /><path d="M12 8 V12 L15 14" /></Svg>
);
export const IconPlantSettings = (p: IconProps) => (
  <Svg {...p}><path d="M3 20 V10 L8 7 V11 L13 8 V11 L18 8 V20 Z" /><path d="M7 16 H9 M12 16 H14" opacity=".5" /><circle cx="19" cy="5" r="2" opacity=".6" /></Svg>
);
export const IconSchemaEdit = (p: IconProps) => (
  <Svg {...p}><path d="M4 4 H9 V8 H4 Z" /><path d="M6.5 8 V17 H10" /><path d="M6.5 12 H10" /><path d="M12 19 L19 12 L21 14 L14 21 H12 Z" /></Svg>
);
export const IconTelemetry = (p: IconProps) => (
  <Svg {...p}><path d="M3 20 H21" opacity=".45" /><path d="M4 16 L9 10 L13 13 L20 5" /><path d="M16 5 H20 V9" /></Svg>
);
export const IconTable = (p: IconProps) => (
  <Svg {...p}><path d="M4 5 H20 V19 H4 Z" /><path d="M4 9.5 H20 M4 14 H20" opacity=".55" /><path d="M10 5 V19" opacity=".55" /></Svg>
);
export const IconNodeAdd = (p: IconProps) => (
  <Svg {...p}><path d="M4 5 H11 V10 H4 Z" /><path d="M7.5 10 V16 H11" /><path d="M17 12 V20 M13 16 H21" /></Svg>
);
/** OEM-Datenpunkte: Schlüssel mit Liste dahinter, der volle Adressraum des Reglers. */
export const IconOem = (p: IconProps) => (
  <Svg {...p}><circle cx="7.5" cy="12" r="3.5" /><path d="M11 12 H20 M17 12 V15 M20 12 V14" /><path d="M4 4 H20 M4 20 H20" opacity=".4" /></Svg>
);

/* ══════════════════════════════════════════════════════════════
   08 · AUSWERTUNG  (24×24) — Werkzeugleiste der Diagramm-Seite
   ══════════════════════════════════════════════════════════════ */

/** Seitenleiste links ein- und ausblenden. */
export const IconSidebar = (p: IconProps) => (
  <Svg {...p}><path d="M4 5 H20 V19 H4 Z" /><path d="M9 5 V19" /><path d="M5.5 9 H7.5 M5.5 12 H7.5 M5.5 15 H7.5" opacity=".55" /></Svg>
);
/** Serien einstellen: Schieberegler. */
export const IconSeries = (p: IconProps) => (
  <Svg {...p}><path d="M4 7 H20 M4 12 H20 M4 17 H20" opacity=".45" /><path d="M9 5 V9 M15 10 V14 M7 15 V19" /></Svg>
);
/** Kennzahlen: Summenzeichen. */
export const IconSigma = (p: IconProps) => (
  <Svg {...p}><path d="M18 6 V4 H6 L12 12 L6 20 H18 V18" /></Svg>
);
export const IconCalendar = (p: IconProps) => (
  <Svg {...p}><path d="M4 6 H20 V20 H4 Z" /><path d="M4 10 H20" /><path d="M8 4 V7 M16 4 V7" /><path d="M8 14 H10 M14 14 H16 M8 17 H10" opacity=".55" /></Svg>
);
/** Live nachladen: Punkt mit Wellen. */
export const IconLive = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" /><path d="M8 8 A5.5 5.5 0 0 0 8 16 M16 8 A5.5 5.5 0 0 1 16 16" /><path d="M5 5 A10 10 0 0 0 5 19 M19 5 A10 10 0 0 1 19 19" opacity=".5" /></Svg>
);
/** Bereich im Diagramm markieren. */
export const IconBrushRange = (p: IconProps) => (
  <Svg {...p}><path d="M8 4 V20 M16 4 V20" /><path d="M8 8 H16 V16 H8" opacity=".35" fill="currentColor" stroke="none" /><path d="M3 15 L6 11 L10 14 L14 9 L21 13" opacity=".6" /></Svg>
);
/** Anmerkungen: Sprechblase. */
export const IconComment = (p: IconProps) => (
  <Svg {...p}><path d="M4 5 H20 V16 H11 L7 20 V16 H4 Z" /><path d="M8 9.5 H16 M8 12.5 H13" opacity=".55" /></Svg>
);
/** Als Bild sichern. */
export const IconImage = (p: IconProps) => (
  <Svg {...p}><path d="M4 5 H20 V19 H4 Z" /><path d="M4 16 L9 11 L13 15 L16 12 L20 16" /><circle cx="15.5" cy="8.5" r="1.5" /></Svg>
);
export const IconFullscreen = (p: IconProps) => (
  <Svg {...p}><path d="M4 9 V4 H9 M15 4 H20 V9 M20 15 V20 H15 M9 20 H4 V15" /></Svg>
);
/** Kopieren: zwei versetzte Blaetter. */
export const IconCopy = (p: IconProps) => (
  <Svg {...p}><path d="M9 9 H20 V20 H9 Z" /><path d="M15 9 V4 H4 V15 H9" opacity=".6" /></Svg>
);
/** Puffer des Field-Clients: gestapelte Speicherscheiben. */
export const IconBuffer = (p: IconProps) => (
  <Svg {...p}><ellipse cx="12" cy="6" rx="7" ry="2.5" /><path d="M5 6 V18 C5 19.4 8.1 20.5 12 20.5 C15.9 20.5 19 19.4 19 18 V6" /><path d="M5 12 C5 13.4 8.1 14.5 12 14.5 C15.9 14.5 19 13.4 19 12" opacity=".55" /></Svg>
);
